import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
import {
  apiSignIn,
  apiSignOut,
  apiSignUp,
  apiVerifyAdminOtp,
  apiResendAdminOtp,
} from "services/AuthService";
import { registerDeviceToken } from "services/FirebaseService";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";

function useAuth() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const query = useQuery();

  const { token, signedIn } = useSelector((state) => state.auth.session);

  const completeSession = (resp) => {
    dispatch(onSignInSuccess(resp));
    const userData = resp?.data || {};
    dispatch(
      setUser({
        ...userData,
        authority: ["ADMIN"],
        role: userData.role || "admin",
        permission: userData.permission || {},
      }),
    );

    const sessionToken = resp?.data?.token ?? resp?.token;

    registerDeviceToken(sessionToken).catch((error) => {
      console.error("Failed to register push notification token:", error);
    });

    const redirectUrl = query.get(REDIRECT_URL_KEY);
    navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
  };

  const signIn = async (values) => {
    try {
      const resp = await apiSignIn(values);
      const d = resp?.data ?? resp;
      // Superadmin: OTP bypassed — session ready (token / otpBypassed).
      // Prefer token/otpBypassed over role so older clients stay compatible.
      const token = d?.token;
      const otpBypassed = d?.otpBypassed === true;
      const slug = d?.slug;

      if (token || otpBypassed) {
        if (!token) {
          return {
            status: "failed",
            message:
              resp?.message ||
              "Login succeeded but no session token was returned.",
          };
        }
        completeSession(resp);
        return {
          status: "success",
          message: "signed in",
        };
      }

      // Admin / subadmin: OTP required — navigate with slug only.
      if (slug) {
        return {
          status: "otp_required",
          slug,
          message: resp?.message || d?.message || "",
        };
      }

      return {
        status: "failed",
        message:
          resp?.message ||
          d?.message ||
          "Unable to sign in. Please try again.",
      };
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const verifyAdminOtp = async (slug, code) => {
    try {
      const resp = await apiVerifyAdminOtp(slug, code);
      const token = resp?.data?.token ?? resp?.token;

      if (token) {
        completeSession(resp);
        return {
          status: "success",
          message: "signed in",
        };
      }

      return {
        status: "failed",
        message:
          resp?.data?.message ||
          resp?.message ||
          "Invalid or expired code.",
      };
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const resendAdminOtp = async (slug) => {
    try {
      const resp = await apiResendAdminOtp(slug);
      return {
        status: "success",
        message:
          resp?.data?.message || resp?.message || "A new code has been sent.",
      };
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };
  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data) {
        const { token } = resp?.data?.token;
        dispatch(onSignInSuccess(token));
        if (resp.data.user) {
          dispatch(
            setUser(
              resp.data.user || {
                avatar: "",
                userName: "Anonymous",
                authority: ["USER"],
                email: "",
              }
            )
          );
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
        return {
          status: "success",
          message: "",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    dispatch(setUser(initialState));
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
  localStorage.removeItem("admin");
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
    verifyAdminOtp,
    resendAdminOtp,
  };
}

export default useAuth;
